import { Typography } from "@mui/material";
import { get } from "lodash";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import AppBar from "src/components/AppBar/AppBar";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import GuardedComponent from "src/components/GuardedComponent/GuardedComponent";
import Icon, { IconName } from "src/components/Icon/Icon";
import FilterContainer from "src/components/TableV2/FilterContainer";
import Title from "src/components/TableV2/Title";
import { IIODncPhone } from "src/store/models/campaignDncPhone.models";
import { UserRole } from "src/store/models/user.model";
import { fetchDncPhoneList, removeDncPhone } from "src/store/reducers/campaignDncPhone";
import { hasAnyAuthority } from "src/util/roleAuthorization";
import { openUrl } from "src/util/util.navigation";
import DisTable from "../../components/TableV2/Table";
import * as S from "../../constants/StringConstants";
import { IRootState } from "../../store/reducers";
import "./CampaignDncPhone.scss";

interface ICampaignDncPhoneProps extends StateProps, DispatchProps, RouteComponentProps {}

interface ICampaignDncPhoneState {
  deletedId: number | null;
  deletedPhone: string | null;
}

class CampaignDncPhone extends React.Component<ICampaignDncPhoneProps, ICampaignDncPhoneState> {
  constructor(props: ICampaignDncPhoneProps) {
    super(props);
    this.state = { deletedId: null, deletedPhone: null };
    this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
    this.handleDeleteCancel = this.handleDeleteCancel.bind(this);
  }

  handleDeletePopup = (id: number, phoneNumber: string) => {
    this.setState({ deletedId: id, deletedPhone: phoneNumber });
  };

  async handleDeleteConfirm() {
    const { deletedId } = this.state;
    if (deletedId !== null) {
      await this.props.removeDncPhone(deletedId);
      await this.props.fetchDncPhoneList();
      this.setState({ deletedId: null, deletedPhone: null });
    }
  }

  handleDeleteCancel() {
    this.setState({ deletedId: null, deletedPhone: null });
  }

  render() {
    const {
      auth,
      fetchDncPhoneList,
      dncPhone: { map, projection, total, listOptions, loading },
    } = this.props;

    const isAdmin = hasAnyAuthority(auth.user, [UserRole.Admin]);

    const columns = [
      {
        key: "phoneNumber",
        title: S.DNC_COL_PHONE,
        sortKey: "phoneNumberId",
      },
      ...(isAdmin
        ? [
            {
              key: "delete",
              title: S.AFF_DELETE,
              style: { textAlign: "right" },
              render: (item: IIODncPhone) => (
                <Icon
                  name={IconName.DeleteFileBlue}
                  size="16px"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleDeletePopup(item.id, item.phoneNumber);
                  }}
                />
              ),
            },
          ]
        : []),
    ];

    return (
      <div className="table-view-v2__content">
        <div className="table-view__header-buttons-container">
          <div className="table-view__header-container">
            <AppBar title={S.DNC_PHONE_TITLE} icon={IconName.Campaigns} loading={loading} />
          </div>

          <div className="table-view__buttons-container">
            <GuardedComponent authorizedRoles={[UserRole.Admin]}>
              <button className="side-nav__add-button" onClick={() => openUrl("/dnc/email/new", true)}>
                <Icon name={IconName.Plus} />
                {S.DNC_NEW_EMAIL}
              </button>

              <button className="side-nav__add-button" onClick={() => openUrl("/dnc/phone/new", true)}>
                <Icon name={IconName.Plus} />
                {S.DNC_NEW_PHONE}
              </button>
            </GuardedComponent>
          </div>
        </div>

        <DisTable<IIODncPhone>
          columns={columns}
          initialState={{ rows: 25, projection, total, map, ...listOptions }}
          onUpdate={async ({ page, rows, sort, query }) => {
            const result: any = await fetchDncPhoneList({ page, rows, sort, query });
            return {
              total: get(result, "value.data.totalRows") as number,
              data: get(result, "value.data.items") as IIODncPhone[],
            };
          }}>
          <FilterContainer left={<Title text={S.DNC_PHONE_TITLE} />}></FilterContainer>
        </DisTable>

        {this.state.deletedId !== null && (
          <PromptModal
            open
            title={S.DNC_PHONE_TITLE}
            confirmText={S.DNC_PHONE_DELETE}
            rejectText={S.DNC_PHONE_CANCEL_DELETE}
            onConfirm={this.handleDeleteConfirm}
            onReject={this.handleDeleteCancel}>
            <Typography variant="h6">{S.DNC_PHONE_DELETE_CONFIRMATION?.(this.state.deletedPhone)}</Typography>
          </PromptModal>
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ dncPhone, auth }: IRootState) => ({ dncPhone, auth });
const mapDispatchToProps = { fetchDncPhoneList, removeDncPhone };

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(CampaignDncPhone);
